import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowUpRight, Loader2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { API, TRADEOFF_URL } from "@/lib/site";
import { setSeo, setJsonLd } from "@/lib/seo";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const mdComponents = {
  h2: (props) => (
    <h2
      className="mt-10 mb-3 font-heading font-700 text-2xl tracking-tight text-brand-navy"
      {...props}
    />
  ),
  h3: (props) => (
    <h3
      className="mt-8 mb-2 font-heading font-600 text-xl text-brand-navy"
      {...props}
    />
  ),
  p: (props) => (
    <p className="mt-4 text-lg leading-relaxed text-slate-600" {...props} />
  ),
  ul: (props) => (
    <ul className="mt-4 space-y-2 list-disc pl-6 text-lg text-slate-600" {...props} />
  ),
  ol: (props) => (
    <ol className="mt-4 space-y-2 list-decimal pl-6 text-lg text-slate-600" {...props} />
  ),
  li: (props) => <li className="leading-relaxed" {...props} />,
  strong: (props) => (
    <strong className="font-semibold text-brand-navy" {...props} />
  ),
  a: (props) => (
    <a
      className="text-brand-blue font-medium hover:underline"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  blockquote: (props) => (
    <blockquote
      className="mt-6 border-l-4 border-brand-blue pl-5 italic text-slate-600"
      {...props}
    />
  ),
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    setStatus("loading");
    axios
      .get(`${API}/blog/posts/${slug}`)
      .then((res) => {
        setPost(res.data);
        setStatus("ready");
        setSeo({
          title: `${res.data.title} | Blue Shuck Consulting LTD`,
          description: res.data.excerpt,
        });
        setJsonLd("blog-post-jsonld", {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: res.data.title,
          description: res.data.excerpt,
          image: res.data.cover_image,
          datePublished: res.data.published_at,
          author: { "@type": "Organization", name: res.data.author },
          publisher: {
            "@type": "Organization",
            name: "Blue Shuck Consulting LTD",
          },
          mainEntityOfPage: window.location.href,
        });
      })
      .catch(() => setStatus("notfound"));
    return () => setJsonLd("blog-post-jsonld", null);
  }, [slug]);

  return (
    <div className="App bg-white min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-32 pb-24">
        <div className="mx-auto max-w-3xl px-6 md:px-10">
          <Link
            data-testid="blogpost-back-link"
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-brand-navy transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            All insights
          </Link>

          {status === "loading" && (
            <div className="mt-20 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
            </div>
          )}

          {status === "notfound" && (
            <div className="mt-16" data-testid="blogpost-notfound">
              <h1 className="font-heading font-800 text-3xl text-brand-navy">
                Post not found
              </h1>
              <p className="mt-3 text-slate-600">
                This article may have moved.{" "}
                <Link to="/blog" className="text-brand-blue hover:underline">
                  Browse all insights
                </Link>
                .
              </p>
            </div>
          )}

          {status === "ready" && post && (
            <article data-testid="blogpost-article">
              <div className="mt-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-wide text-brand-blue">
                <span>{post.category}</span>
                <span className="text-slate-300">•</span>
                <span className="text-slate-400 normal-case font-medium tracking-normal">
                  {post.read_time}
                </span>
              </div>
              <h1 className="mt-3 font-heading font-800 text-4xl sm:text-5xl tracking-tight leading-[1.1] text-brand-navy">
                {post.title}
              </h1>
              <p className="mt-4 text-sm text-slate-500">
                {post.author} · {formatDate(post.published_at)}
              </p>

              <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200">
                <img
                  src={post.cover_image}
                  alt={post.title}
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="mt-6">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={mdComponents}
                >
                  {post.content}
                </ReactMarkdown>
              </div>

              <div className="mt-12 rounded-2xl bg-brand-navy p-8 text-white">
                <h3 className="font-heading font-700 text-2xl">
                  See Trade Off in action
                </h3>
                <p className="mt-2 text-slate-300">
                  Our take-off to billing tool for construction SMEs. Start free
                  on your first project.
                </p>
                <a
                  data-testid="blogpost-tradeoff-cta"
                  href={TRADEOFF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 bg-white text-brand-navy font-semibold px-6 py-3 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  View Trade Off
                  <ArrowUpRight className="h-5 w-5" />
                </a>
              </div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
